import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, output, signal } from '@angular/core';
import { CreateBriefService } from '../../pages/briefs/service/create-brief/create-brief.service';
import { ViewportOverlayDirective } from '../../common/directives/viewport-overlay/viewport-overlay.directive';

/** A single publishing stage shown in the launch sequence. */
interface LaunchStep {
  label: string;
}

/** Step cadence in ms (780ms default / 260ms reduced-motion). */
export const LAUNCH_STEP_MS = 780;
export const LAUNCH_STEP_MS_REDUCED = 260;

@Component({
  selector: 'atom-loading-overlay-progress',
  imports: [],
  hostDirectives: [ViewportOverlayDirective],
  templateUrl: './loading-overlay-progress.component.html',
  styleUrl: './loading-overlay-progress.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoadingOverlayProgressComponent implements OnDestroy {

  /** Services */
  public createBriefService = inject(CreateBriefService);
  /** Services - END  */

  /** The brief title typed by the user, for the subtitle line. */
  public briefTitle = computed<string>(
    () => this.createBriefService.basicInfoModel().briefTitle?.trim() || 'your brief'
  );

  /** Publishing stages (design copy — see launch-sequence handoff). */
  public readonly steps: readonly LaunchStep[] = [
    { label: 'Packaging your brief' },
    { label: 'Running brand-safety & compliance' },
    { label: 'Matching your creator network' },
    { label: 'Publishing to the network' },
  ];

  /** Equalizer bars — count only; per-bar height/delay live in CSS (nth-child). */
  public readonly bars: readonly number[] = [0, 1, 2, 3, 4, 5, 6];

  /** Index of the currently-active step; loops while the overlay is mounted. */
  public activeStep = signal<number>(0);

  /** Emitted once after the first full step cycle (all stages marked done). */
  cycleComplete = output<void>();

  private timer: ReturnType<typeof setInterval> | undefined;
  private cycleTimer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    const reduce =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const stepMs = reduce ? LAUNCH_STEP_MS_REDUCED : LAUNCH_STEP_MS;

    this.timer = setInterval(() => {
      this.activeStep.update((i) => (i + 1) % (this.steps.length + 1));
    }, stepMs);

    this.cycleTimer = setTimeout(() => {
      this.cycleComplete.emit();
    }, stepMs * this.steps.length);
  }

  ngOnDestroy(): void {
    if (this.timer !== undefined) {
      clearInterval(this.timer);
    }
    if (this.cycleTimer !== undefined) {
      clearTimeout(this.cycleTimer);
    }
  }
}
